/**
 * Clinic profile & configuration (design 2A refined by P11 in place):
 * view-first cards, per-section edit, confirm-first calendar change, the
 * clinic's default travel allowance (ZOY-252, minutes each way for at-home
 * bookings), readonly-degrades-honestly. Settings PATCH sends only what
 * changed — an unchanged member is omitted, never null (ZOY-287). A failed
 * save keeps the editor mounted with everything typed and names the refusal
 * honestly (ZOY-372) — the form never closes on failure.
 */

import { useState } from 'react';
import { parseNumericInput } from '@/core/i18n/digits';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Notice } from '@/ui/kit';
import {
  ConfirmDialog,
  FieldGrid,
  FormCard,
  SelectField,
  Span2,
  TextAreaField,
  Toggle,
} from '@/ui/formkit';
import { TextField } from '@/ui/kit';
import { SaveErrorNotice, saveErrorOf, type SaveError } from './saveError';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { fetchClinic, type ClinicView } from '@/core/clinic/api';
import { patchClinicProfile, patchClinicSettings } from '@/core/clinic/setupApi';
import { settingsPatch, TRAVEL_MAX, TRAVEL_MIN } from './settingsRules';
import { qk } from '@/core/api/keys';

type Mode = 'view' | 'profile' | 'config';

export function ProfileTab() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const queryClient = useQueryClient();
  const manage = authz.can(PERM.settingsManage);

  const clinicQuery = useQuery({
    queryKey: authz.clinicId ? qk.clinic(authz.clinicId) : ['clinic', 'none'],
    queryFn: () => fetchClinic(),
    enabled: authz.clinicId !== null,
    staleTime: 5 * 60_000,
  });
  const clinic = clinicQuery.data;

  const [mode, setMode] = useState<Mode>('view');
  const [saved, setSaved] = useState(false);

  const invalidate = () => {
    if (authz.clinicId) void queryClient.invalidateQueries({ queryKey: qk.clinic(authz.clinicId) });
  };

  if (!clinic) {
    return <p className={a.pageSub}>{t('common.loading')}</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxInlineSize: 860 }}>
      <p className={a.pageSub}>{t('profile.sub')}</p>
      {!manage && <Notice kind="info">{t('profile.readonlyNote')}</Notice>}
      {saved && <Notice kind="ok">{t('profile.saved')}</Notice>}

      {mode === 'view' && (
        <div className={a.cardsRow} style={{ inlineSize: '100%' }}>
          <ViewCard
            title={t('profile.cardProfile')}
            meta={t('profile.cardProfileMeta')}
            rows={[
              [t('profile.displayName'), clinic.displayName ?? clinic.clinicId],
              [t('profile.address'), clinic.address ?? '—'],
              [t('profile.contactPhone'), clinic.contactPhone ?? '—'],
              [t('profile.contactEmail'), clinic.contactEmail ?? '—'],
              [t('profile.website'), clinic.website ?? '—'],
              [t('profile.instagram'), clinic.instagram ? `@${clinic.instagram}` : '—'],
              [t('profile.operatingContext'), clinic.operatingContext ?? '—'],
            ]}
            action={
              manage
                ? { label: t('profile.editProfile'), onClick: () => setMode('profile') }
                : undefined
            }
          />
          <ViewCard
            title={t('profile.cardConfig')}
            meta={t('profile.cardConfigMeta')}
            rows={[
              [
                t('profile.calendar'),
                clinic.settings.calendarSystem === 'jalali'
                  ? t('profile.jalali')
                  : t('profile.gregorian'),
              ],
              [t('profile.currency'), clinic.settings.currencyCode],
              [t('profile.timezone'), clinic.settings.timezone],
              [
                t('profile.buffer'),
                `${intl.formatNumber(clinic.settings.defaultBufferMinutes)} ${t('common.minutes')}`,
              ],
              [
                t('profile.travel'),
                t('profile.travelValue', {
                  n: intl.formatNumber(clinic.settings.defaultTravelAllowanceMinutes),
                }),
              ],
              // tasks#138: only for managers, and only when the read carries the field.
              ...(manage && clinic.settings.practitionerScheduleSms !== undefined
                ? [
                    [
                      t('profile.scheduleSms'),
                      clinic.settings.practitionerScheduleSms
                        ? t('common.enabled')
                        : t('common.disabled'),
                    ] as [string, string],
                  ]
                : []),
            ]}
            action={
              manage
                ? { label: t('profile.editConfig'), onClick: () => setMode('config') }
                : undefined
            }
          />
        </div>
      )}

      {mode === 'profile' && (
        <ProfileEdit
          clinic={clinic}
          onSaved={() => {
            setMode('view');
            setSaved(true);
            invalidate();
          }}
          onCancel={() => setMode('view')}
        />
      )}

      {mode === 'config' && (
        <ConfigEdit
          clinic={clinic}
          onSaved={() => {
            setMode('view');
            setSaved(true);
            invalidate();
          }}
          onCancel={() => setMode('view')}
        />
      )}
    </div>
  );
}

function ViewCard(props: {
  title: string;
  meta: string;
  rows: [string, string][];
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div style={{ padding: '16px 18px 6px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--zc-teal-deep)' }}>
          {props.title}
        </h2>
        <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 2 }}>
          {props.meta}
        </p>
      </div>
      {props.rows.map(([k, v]) => (
        <div key={k} className={a.row}>
          <span className={a.rowKey}>{k}</span>
          <span className={a.rowValue}>{v}</span>
        </div>
      ))}
      {props.action && (
        <div style={{ padding: '10px 18px 16px' }}>
          <Button variant="quiet" onClick={props.action.onClick}>
            {props.action.label}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ProfileEdit(props: {
  clinic: ClinicView;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const c = props.clinic;
  const [displayName, setDisplayName] = useState(c.displayName ?? '');
  const [address, setAddress] = useState(c.address ?? '');
  const [phone, setPhone] = useState(c.contactPhone ?? '');
  const [email, setEmail] = useState(c.contactEmail ?? '');
  const [context, setContext] = useState(c.operatingContext ?? '');
  const [website, setWebsite] = useState(c.website ?? '');
  const [instagram, setInstagram] = useState(c.instagram ?? '');
  const [error, setError] = useState<SaveError | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      patchClinicProfile({
        displayName: displayName || undefined,
        address: address || undefined,
        contactPhone: phone || undefined,
        contactEmail: email || undefined,
        operatingContext: context || undefined,
        // '' is meaningful here: it clears a link the clinic no longer wants shown.
        website: website.trim() === (c.website ?? '') ? undefined : website.trim(),
        instagram: instagram.trim() === (c.instagram ?? '') ? undefined : instagram.trim(),
      }),
    onMutate: () => setError(null),
    onSuccess: () => props.onSaved(),
    // ZOY-372: stay mounted — everything typed is still here for a retry.
    onError: (err) => setError(saveErrorOf(err)),
  });

  return (
    <FormCard
      title={t('profile.cardProfile')}
      onSubmit={() => mutation.mutate()}
      cta={t('common.save')}
      busy={mutation.isPending}
      secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
    >
      {error && <SaveErrorNotice error={error} />}
      <FieldGrid>
        <Span2>
          <TextField
            label={t('profile.displayName')}
            value={displayName}
            onChange={setDisplayName}
            help={t('profile.displayNameHelp')}
          />
        </Span2>
        <TextField
          label={t('profile.contactPhone')}
          value={phone}
          onChange={setPhone}
          help={t('profile.contactPhoneHelp')}
          machine
          type="tel"
        />
        <TextField
          label={t('profile.contactEmail')}
          value={email}
          onChange={setEmail}
          machine
          type="email"
        />
        <TextField
          label={t('profile.website')}
          value={website}
          onChange={setWebsite}
          help={t('profile.websiteHelp')}
          machine
          type="url"
        />
        <TextField
          label={t('profile.instagram')}
          value={instagram}
          onChange={setInstagram}
          help={t('profile.instagramHelp')}
          machine
        />
        <Span2>
          <TextAreaField
            label={t('profile.address')}
            value={address}
            onChange={setAddress}
            help={t('profile.addressHelp')}
            rows={2}
          />
        </Span2>
        <Span2>
          <TextAreaField
            label={t('profile.operatingContext')}
            value={context}
            onChange={setContext}
            help={t('profile.operatingContextHelp')}
            rows={2}
            requirement={{ word: t('signin.optional'), kind: 'optional' }}
          />
        </Span2>
      </FieldGrid>
    </FormCard>
  );
}

export function ConfigEdit(props: {
  clinic: ClinicView;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const st = props.clinic.settings;
  const [calendar, setCalendar] = useState(st.calendarSystem);
  const [currency, setCurrency] = useState(st.currencyCode);
  const [timezone, setTimezone] = useState(st.timezone);
  const [buffer, setBuffer] = useState(String(st.defaultBufferMinutes));
  const [travel, setTravel] = useState(String(st.defaultTravelAllowanceMinutes));
  // The `?? false` seed is inert: settingsPatch never sends the key when the read lacks it.
  const [scheduleSms, setScheduleSms] = useState(st.practitionerScheduleSms ?? false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);

  const calendarChanged = calendar !== st.calendarSystem;
  const travelNumber = parseNumericInput(travel) ?? NaN;
  const travelValid =
    travel.trim() !== '' &&
    Number.isInteger(travelNumber) &&
    travelNumber >= TRAVEL_MIN &&
    travelNumber <= TRAVEL_MAX;
  // ZOY-373: the helper states "a 3-letter ISO code" — enforce exactly that
  // shape before anything is sent, the way the travel field enforces its rule.
  const currencyValid = /^[A-Z]{3}$/.test(currency);

  const mutation = useMutation({
    mutationFn: () =>
      patchClinicSettings(
        settingsPatch(st, {
          calendarSystem: calendar as 'jalali' | 'gregorian',
          currencyCode: currency,
          timezone,
          defaultBufferMinutes: Number(buffer) || 0,
          defaultTravelAllowanceMinutes: travelNumber,
          practitionerScheduleSms: scheduleSms,
        }),
      ),
    onMutate: () => setError(null),
    onSuccess: () => props.onSaved(),
    // ZOY-372: stay mounted — everything typed is still here for a retry.
    onError: (err) => setError(saveErrorOf(err)),
  });

  const submit = () => {
    // Consequential presentation change confirms first (frozen P11 decision).
    if (calendarChanged) setConfirming(true);
    else mutation.mutate();
  };

  return (
    <>
      <FormCard
        title={t('profile.cardConfig')}
        onSubmit={submit}
        cta={t('common.save')}
        ctaDisabled={!travelValid || !currencyValid}
        busy={mutation.isPending}
        secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
      >
        {error && <SaveErrorNotice error={error} />}
        <FieldGrid>
          <SelectField
            label={t('profile.calendar')}
            value={calendar}
            onChange={(v) => setCalendar(v as typeof calendar)}
            options={[
              { value: 'jalali', label: t('profile.jalali') },
              { value: 'gregorian', label: t('profile.gregorian') },
            ]}
            help={t('profile.calendarHelp')}
          />
          <SelectField
            label={t('profile.buffer')}
            value={buffer}
            onChange={setBuffer}
            options={['0', '5', '10', '15'].map((v) => ({
              value: v,
              label: `${intl.formatNumber(Number(v))} ${t('common.minutes')}`,
            }))}
            help={t('profile.bufferHelp')}
          />
          <TextField
            label={t('profile.travel')}
            value={travel}
            onChange={setTravel}
            help={t('profile.travelHelp', {
              min: intl.formatNumber(TRAVEL_MIN),
              max: intl.formatNumber(TRAVEL_MAX),
            })}
            error={travelValid ? undefined : t('profile.travelInvalid')}
            machine
            inputMode="numeric"
            prefix={t('common.minutes')}
          />
          <TextField
            label={t('profile.currency')}
            value={currency}
            onChange={(v) => setCurrency(v.toUpperCase())}
            help={t('profile.currencyHelp')}
            error={currencyValid ? undefined : t('profile.currencyInvalid')}
            machine
          />
          <TextField
            label={t('profile.timezone')}
            value={timezone}
            onChange={setTimezone}
            help={t('profile.timezoneHelp')}
            machine
          />
          {st.practitionerScheduleSms !== undefined && (
            <Span2>
              <Toggle
                label={t('profile.scheduleSms')}
                help={t('profile.scheduleSmsNotice')}
                checked={scheduleSms}
                onChange={setScheduleSms}
                stateWord={scheduleSms ? t('common.enabled') : t('common.disabled')}
              />
            </Span2>
          )}
        </FieldGrid>
      </FormCard>
      <ConfirmDialog
        open={confirming}
        title={t('profile.confirmCalTitle')}
        body={t('profile.confirmCalBody', {
          calendar: calendar === 'jalali' ? t('profile.jalali') : t('profile.gregorian'),
        })}
        effect={t('profile.confirmCalEffect')}
        confirmLabel={t('profile.confirmCalCta')}
        busy={mutation.isPending}
        onConfirm={() => {
          setConfirming(false);
          mutation.mutate();
        }}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}
